import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import { Params } from 'next/dist/server/router';
import { captureException } from '@sentry/nextjs';

import { LegalPage } from '@components/LegalPage';
import { LegalPageProps } from '@components/LegalPage/LegalPage';

import {
	getAllLegalPageNavData,
	getAllLegalPageSlugs,
	getLegalPage,
} from '@data/sanity/queries';
import { SideNavSection } from '@interfaces/common';
import { replaceUnderscoreWithDash } from '@utils/locale';
import { withSiteWideData } from '@utils/page';

const Legal: React.FC<LegalPageProps> = ({ legalPage, sideNavData }) => {
	return <LegalPage legalPage={legalPage} sideNavData={sideNavData} />;
};

export default Legal;

export const getStaticProps: GetStaticProps = withSiteWideData<LegalPageProps>(
	async (context: GetStaticPropsContext) => {
		const contextParams = context.params as Params;
		const { locale, slug: slugParam } = contextParams;
		const legalPage = await getLegalPage(locale, slugParam);
		const sideNavData = await getAllLegalPageNavData(locale);

		const mappedSideNavData: SideNavSection[] = sideNavData.map(
			({ title, params }) => ({
				text: title,
				href: `/legal/${params.slug}`,
			})
		);

		if (!legalPage) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				metadata: legalPage.metadata || null,
				legalPage,
				sideNavData: mappedSideNavData,
				hasNewsLetterSection: false,
			},
		};
	}
);

export const getStaticPaths: GetStaticPaths = async () => {
	try {
		const paths = await getAllLegalPageSlugs();

		return {
			paths: paths.map(({ locale, params }) => ({
				locale: replaceUnderscoreWithDash(locale),
				params,
			})),
			fallback: false,
		};
	} catch (error) {
		captureException(error);
		return {
			paths: [],
			fallback: false,
		};
	}
};
